/**
 * WallSketch Hover
 *
 * Single source of truth for "what skeleton element is under the cursor".
 * Owned by `PointoramaWallSketch` (via `getHover()`), so every tool that
 * runs hit-testing on the skeleton writes its result here instead of
 * carrying its own `hoveredNodeId` / `hoveredEdgeId` field. Subscribers
 * (tooltip overlays, status bars) read from one place.
 *
 * Why this lives alongside selection rather than inside `WallSketchSelection`:
 *  - Hover is at-most-one (node XOR edge); selection is many.
 *  - Hover has no persistent visual (the cursor itself is the visual),
 *    only a transient marker highlight that this class owns.
 *  - Subscribers want hover updates without paying the selection event
 *    cost on every mousemove.
 *
 * Visuals owned here:
 *  - Hovered-node marker highlight (the bright outline `SphereMarker.highlight()`).
 *    Unhighlighted automatically when hover moves off the node.
 *
 * Visuals NOT owned here:
 *  - Edge hover overlay — edges have no per-edge mesh today, and the
 *    pixel-distance hit-test already produces a tight enough match that
 *    a separate highlight isn't needed for the editor's current UX. Add
 *    if a future tool wants one.
 */

import { PointoramaWallSketch } from '../../../../utils/PointoramaWallSketch';
import type { Vector3Like } from 'three';

export interface WallSketchHoverDeps {
  getWallSketch: () => PointoramaWallSketch | null;
  /**
   * Called whenever the hovered element changes. Fires on transitions
   * (node → none, none → edge, node A → node B, etc.) but NOT on
   * point-only updates inside the same edge (those are cursor drift along
   * the same hit and would spam subscribers). Consumers needing the live
   * point can read `getHoveredPoint()` on demand.
   */
  onHoverChanged?: () => void;
}

/** Snapshot of the current hover target. At most one of `nodeId`/`edgeId` is non-null. */
export interface HoverSnapshot {
  nodeId: string | null;
  edgeId: string | null;
  /** Projected world-space point on the hovered edge; null for node or no hover. */
  point: Vector3Like | null;
}

export class WallSketchHover {
  private deps: WallSketchHoverDeps;
  private hoveredNodeId: string | null = null;
  private hoveredEdgeId: string | null = null;
  private hoveredPoint: Vector3Like | null = null;

  constructor(deps: WallSketchHoverDeps) {
    this.deps = deps;
  }

  // ─── Read access ────────────────────────────────────────────────────

  getHoveredNodeId(): string | null {
    return this.hoveredNodeId;
  }

  getHoveredEdgeId(): string | null {
    return this.hoveredEdgeId;
  }

  /** Projected world-space point on the hovered edge, or null. */
  getHoveredPoint(): Vector3Like | null {
    return this.hoveredPoint;
  }

  /** Convenience: full snapshot for subscribers that want both fields atomically. */
  getSnapshot(): HoverSnapshot {
    return { nodeId: this.hoveredNodeId, edgeId: this.hoveredEdgeId, point: this.hoveredPoint };
  }

  hasHover(): boolean {
    return this.hoveredNodeId !== null || this.hoveredEdgeId !== null;
  }

  // ─── Mutation ───────────────────────────────────────────────────────

  /**
   * Set the hovered node (clears any edge hover). Idempotent — passing
   * the already-hovered id is a no-op (no subscriber notification).
   */
  setNode(nodeId: string | null): void {
    if (this.hoveredNodeId === nodeId && this.hoveredEdgeId === null) return;
    this.applyNodeUnhighlight();
    this.hoveredNodeId = nodeId;
    this.hoveredEdgeId = null;
    this.hoveredPoint = null;
    this.applyNodeHighlight();
    this.deps.onHoverChanged?.();
  }

  /**
   * Set the hovered edge + optional projected point. Clears any node hover.
   * The point is updated on every call (no notification), but a change of
   * `edgeId` does fire `onHoverChanged`.
   */
  setEdge(edgeId: string | null, point: Vector3Like | null = null): void {
    const edgeChanged = this.hoveredEdgeId !== edgeId || this.hoveredNodeId !== null;
    if (!edgeChanged) {
      // Same edge as last call — only the point moved. Update silently.
      this.hoveredPoint = point;
      return;
    }
    this.applyNodeUnhighlight();
    this.hoveredNodeId = null;
    this.hoveredEdgeId = edgeId;
    this.hoveredPoint = point;
    this.deps.onHoverChanged?.();
  }

  /** Clear all hover state. */
  clear(): void {
    if (this.hoveredNodeId === null && this.hoveredEdgeId === null) return;
    this.applyNodeUnhighlight();
    this.hoveredNodeId = null;
    this.hoveredEdgeId = null;
    this.hoveredPoint = null;
    this.deps.onHoverChanged?.();
  }

  /**
   * Drop hover that points at a deleted element. Call after a topology
   * mutation. Returns true when something was cleared.
   */
  clearStale(deletedNodeId: string | null, deletedEdgeId: string | null): boolean {
    let changed = false;
    if (deletedNodeId && this.hoveredNodeId === deletedNodeId) {
      this.applyNodeUnhighlight();
      this.hoveredNodeId = null;
      changed = true;
    }
    if (deletedEdgeId && this.hoveredEdgeId === deletedEdgeId) {
      this.hoveredEdgeId = null;
      this.hoveredPoint = null;
      changed = true;
    }
    if (changed) this.deps.onHoverChanged?.();
    return changed;
  }

  /**
   * Reapply the node highlight visual against the current skeleton's
   * markers. Call after `skeleton.setData()` rebuilds the marker meshes
   * — the previous reference is gone and the new marker doesn't know
   * it should be highlighted.
   */
  reapplyVisuals(): void {
    this.applyNodeHighlight();
  }

  // ─── Visuals ────────────────────────────────────────────────────────

  private applyNodeHighlight(): void {
    if (!this.hoveredNodeId) return;
    const skeleton = this.deps.getWallSketch();
    skeleton?.getNodeMarker(this.hoveredNodeId)?.highlight();
  }

  private applyNodeUnhighlight(): void {
    if (!this.hoveredNodeId) return;
    const skeleton = this.deps.getWallSketch();
    skeleton?.getNodeMarker(this.hoveredNodeId)?.unHighlight();
  }
}
